import { OrgFlagTag } from "../common/noc-flag";
import { useUiStrings } from "@seagames/ui";
import type { ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { errorMessage } from "../../lib/api/messages";
import type { AccommodationFlag, LodgingSex, RoomingContingent, Stay, StaySource } from "../../lib/accommodation/types";
import { formatDayShort } from "../../lib/format/datetime";
import { CalendarIcon } from "./icons";
import "./accommodation.css";

/**
 * Small read-only pieces every accommodation screen shares: the progress
 * bar, a stay, a room, the organisation tag, a sex letter. Values only — any
 * sentence around them is a key.
 */

export { EmptyBlock, Section } from "../transport/parts";

/** Her `bar(a, b)`: a thin track, navy (or green, `ok`) filled to a / b. */
export function Bar({ value, max, tone, className = "", label }: { value: number; max: number; tone?: "ok"; className?: string; label?: string }) {
	const pct = max > 0 ? Math.min(100, Math.round((value / max) * 100)) : 0;
	return (
		<span
			className={`acc-bar ${className}`}
			data-tone={tone}
			role={label ? "meter" : undefined}
			aria-label={label}
			aria-valuemin={label ? 0 : undefined}
			aria-valuemax={label ? max : undefined}
			aria-valuenow={label ? value : undefined}
			aria-hidden={label ? undefined : true}
		>
			<i style={{ width: `${pct}%` }} />
		</span>
	);
}

export const percent = (a: number, b: number): number => (b > 0 ? Math.round((a / b) * 100) : 0);

/**
 * "Mon 20 Sep – Tue 28 Sep": first night → check-out day (`[from, to)`).
 * Dates are calendar dates in the games' zone, no clock.
 */
export function StayRange({ stay, muted = false, className = "" }: { stay: Stay | null; muted?: boolean; className?: string }) {
	const { t } = useTranslation("accommodation");
	if (!stay) return <span className={`text-gms-ink-3 ${className}`}>—</span>;
	return (
		<span className={`whitespace-nowrap tabular-nums ${muted ? "text-gms-ink-3" : ""} ${className}`}>
			{t("stay.range", { from: formatDayShort(stay.from), to: formatDayShort(stay.to) })}
		</span>
	);
}

/** Where a stay's dates come from (travel, the contingent's default dates, typed by the host). */
export function StaySourceNote({ source }: { source: StaySource }) {
	const { t } = useTranslation("accommodation");
	return <span className="text-[0.75rem] text-gms-ink-3">{t(`stay.source.${source}`)}</span>;
}

/** The flags that mean "act now" (red); the rest are warnings (amber). */
export const BAD_FLAGS: ReadonlySet<AccommodationFlag> = new Set(["CARD_WITHDRAWN", "OVERDUE"]);

/** One flag pill (`ACCOMMODATION_FLAGS`): label, the one-line meaning in the tooltip. */
export function FlagTag({ flag }: { flag: AccommodationFlag }) {
	const { t } = useTranslation("accommodation");
	return (
		<span
			title={t(`flag.hint.${flag}`)}
			className={`inline-flex h-[1.25rem] items-center gap-[0.25rem] rounded-[0.25rem] px-[0.375rem] text-[0.6875rem] font-semibold whitespace-nowrap ${
				BAD_FLAGS.has(flag) ? "bg-gms-bad-soft text-gms-bad" : "bg-gms-warn-soft text-gms-warn"
			}`}
		>
			{flag === "STAY_DIFFERS" && <CalendarIcon className="size-[0.75rem]" />}
			{t(`flag.label.${flag}`)}
		</span>
	);
}

/** A row's flags (or only some of them), in the contract's order. */
export function FlagTags({ flags, only }: { flags: readonly AccommodationFlag[]; only?: readonly AccommodationFlag[] }) {
	const shown = only ? flags.filter((f) => only.includes(f)) : flags;
	if (shown.length === 0) return null;
	return (
		<span className="flex flex-wrap gap-[0.25rem]">
			{shown.map((f) => (
				<FlagTag key={f} flag={f} />
			))}
		</span>
	);
}

/**
 * The room gap of a contingent (her "Short 3 M, 2 F"): men / women who need a
 * bed (an arrival on record, or a bed already — `need_room_*`) without a
 * reserved bed of their sex, after the either-sex beds are counted in.
 */
export function roomGap(
	r: Pick<RoomingContingent, "need_room_male" | "need_room_female" | "reserved_beds_male" | "reserved_beds_female" | "reserved_beds_any">,
): {
	men: number;
	women: number;
} {
	let men = Math.max(0, r.need_room_male - r.reserved_beds_male);
	let women = Math.max(0, r.need_room_female - r.reserved_beds_female);
	let any = r.reserved_beds_any;
	const m = Math.min(any, men);
	men -= m;
	any -= m;
	women -= Math.min(any, women);
	return { men, women };
}

/** The organisation's code as a tag, the name in the tooltip (her flag + code). */
export function OrgCode({ org }: { org: { code: string | null; name: string } }) {
	return <OrgFlagTag code={org.code} name={org.name} />;
}

/** A building's short code (0084, the office sets it), a mono tag beside its name. */
export function BuildingCode({ code }: { code: string }) {
	return (
		<span className="shrink-0 rounded-[0.25rem] border border-gms-line-2 px-[0.3125rem] font-mono text-[0.6875rem] leading-[1.4] font-semibold text-gms-ink-2">
			{code}
		</span>
	);
}

/**
 * Her sex letter on a room tile ("M" / "W"); the word is the tooltip. `null`
 * is a room with no restriction, "NR" (user, 2026-11-03: M / W / NR).
 */
export function SexLetter({ sex }: { sex: LodgingSex | null }) {
	const { t } = useTranslation("accommodation");
	const word = sex ? t(`sex.${sex}`) : t("roomSheet.noRestriction");
	return (
		<span className="acc-sex" title={word}>
			<span aria-hidden>{t(`sex.letter.${sex ?? "NONE"}`)}</span>
			<span className="sr-only">{word}</span>
		</span>
	);
}

/** "322 (140 / 182)": a total with men / women after it, muted. */
export function SplitCount({ total, male, female }: { total: number; male: number; female: number }) {
	const { formatNumber: fmt } = useUiStrings();
	const { t } = useTranslation("accommodation");
	return (
		<span className="tabular-nums" title={t("rooming.split", { male: fmt(male), female: fmt(female) })}>
			{fmt(total)} <span className="text-gms-ink-3">({t("rooming.splitShort", { male: fmt(male), female: fmt(female) })})</span>
		</span>
	);
}

/** Her loading rows. */
export function SkeletonRows({ n = 3, h = "3.25rem" }: { n?: number; h?: string }) {
	return (
		<div className="grid gap-[0.5rem] p-[0.75rem]" aria-busy>
			{Array.from({ length: n }, (_, i) => (
				// biome-ignore lint/suspicious/noArrayIndexKey: static placeholders
				<div key={i} className="skeleton rounded-[0.5rem]" style={{ height: h }} />
			))}
		</div>
	);
}

/** A load error inside a card, with Try again. */
export function LoadFailed({ error, onRetry }: { error: unknown; onRetry: () => void }) {
	const { t } = useTranslation("accommodation");
	return (
		<div role="alert" className="grid justify-items-center gap-[0.5rem] px-[1rem] py-[1.5rem] text-center">
			<b className="text-[0.9375rem] text-gms-navy-ink">{t("common.loadFailed")}</b>
			<ErrorText error={error} />
			<button type="button" className="btn btn-sm btn-outline mt-[0.25rem]" onClick={onRetry}>
				{t("common.tryAgain")}
			</button>
		</div>
	);
}

function ErrorText({ error }: { error: unknown }) {
	return <p className="text-[0.8125rem] text-gms-ink-3">{errorMessage(error)}</p>;
}

/** Her outlined small header button (`btn btn--line btn--sm`), icon + label. */
export function LineButton({
	children,
	icon,
	onClick,
	disabled,
	title,
	primary = false,
}: {
	children: ReactNode;
	icon?: ReactNode;
	onClick: () => void;
	disabled?: boolean;
	title?: string;
	primary?: boolean;
}) {
	return (
		<button
			type="button"
			onClick={onClick}
			disabled={disabled}
			title={title}
			className={`btn btn-sm gap-[0.375rem] [&>svg]:size-[1rem] ${primary ? "border-gms-navy bg-gms-navy text-white hover:bg-gms-navy-lift" : "btn-outline"}`}
		>
			{icon}
			{children}
		</button>
	);
}
